import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { parseDRFError } from '../../utils/apiUtils';

// Default options for quick setup
const DEFAULT_OPTIONS = {
  leadStatuses: [
    { name: 'New Lead', slug: 'new-lead', description: 'New lead, not yet contacted', order: 0, applies_to: 'lead', is_active: true },
    { name: 'Contacted', slug: 'contacted', description: 'Initial contact made', order: 1, applies_to: 'lead', is_active: true },
    { name: 'Engaging', slug: 'engaging', description: 'In conversation, gathering info', order: 2, applies_to: 'lead', is_active: true },
    { name: 'Qualified', slug: 'qualified', description: 'Qualified as potential opportunity', order: 3, applies_to: 'lead', is_active: true },
    { name: 'Disqualified', slug: 'disqualified', description: 'Not a good fit', order: 4, applies_to: 'lead', is_active: true },
    { name: 'Unresponsive', slug: 'unresponsive', description: 'Not responding to outreach', order: 5, applies_to: 'lead', is_active: true },
  ],
  opportunityStatuses: [
    { name: 'New Opportunity', slug: 'new-opportunity', description: 'Converted from qualified lead', order: 10, applies_to: 'opportunity', is_active: true },
    { name: 'Discovery', slug: 'discovery', description: 'Understanding needs and requirements', order: 11, applies_to: 'opportunity', is_active: true },
    { name: 'Proposal', slug: 'proposal', description: 'Proposal or quote sent', order: 12, applies_to: 'opportunity', is_active: true },
    { name: 'Negotiation', slug: 'negotiation', description: 'In negotiation phase', order: 13, applies_to: 'opportunity', is_active: true },
    { name: 'Verbal Agreement', slug: 'verbal-agreement', description: 'Verbal commitment received', order: 14, applies_to: 'opportunity', is_active: true },
    { name: 'Won', slug: 'won', description: 'Deal closed successfully', order: 15, applies_to: 'opportunity', is_active: true },
    { name: 'Lost', slug: 'lost', description: 'Deal lost or declined', order: 16, applies_to: 'opportunity', is_active: true },
  ],
  priorities: [
    { name: 'Low', slug: 'low', description: 'Low priority', level: 1, color: '#6c757d', is_active: true },
    { name: 'Medium', slug: 'medium', description: 'Medium priority', level: 2, color: '#0d6efd', is_active: true },
    { name: 'High', slug: 'high', description: 'High priority', level: 3, color: '#ffc107', is_active: true },
    { name: 'Urgent', slug: 'urgent', description: 'Urgent - immediate attention required', level: 4, color: '#dc3545', is_active: true },
  ],
  activityTypes: [
    { name: 'Phone Call', slug: 'phone-call', description: 'Phone conversation', icon: 'phone', is_active: true },
    { name: 'Email', slug: 'email', description: 'Email communication', icon: 'envelope', is_active: true },
    { name: 'LinkedIn Message', slug: 'linkedin-message', description: 'LinkedIn direct message', icon: 'linkedin', is_active: true },
    { name: 'Meeting', slug: 'meeting', description: 'In-person or virtual meeting', icon: 'calendar', is_active: true },
    { name: 'Demo', slug: 'demo', description: 'Product demonstration', icon: 'desktop', is_active: true },
  ],
  outcomeTypes: [
    { name: 'Successful Contact', slug: 'successful-contact', description: 'Successfully reached and engaged', is_positive: true, color: '#28a745', is_active: true },
    { name: 'Left Voicemail', slug: 'left-voicemail', description: 'Left a voicemail message', is_positive: false, color: '#17a2b8', is_active: true },
    { name: 'No Response', slug: 'no-response', description: 'No response received', is_positive: false, color: '#6c757d', is_active: true },
    { name: 'Callback Requested', slug: 'callback-requested', description: 'Prospect requested callback', is_positive: true, color: '#17a2b8', is_active: true },
    { name: 'Not Interested', slug: 'not-interested', description: 'Prospect declined interest', is_positive: false, color: '#dc3545', is_active: true },
    { name: 'Meeting Scheduled', slug: 'meeting-scheduled', description: 'Follow-up meeting scheduled', is_positive: true, color: '#28a745', is_active: true },
  ],
  leadSources: [
    { name: 'Job Board', slug: 'job-board', description: 'Job listing from job boards', icon: 'briefcase', order: 0, is_active: true },
    { name: 'Cold Call', slug: 'cold-call', description: 'Cold calling outreach', icon: 'phone', order: 1, is_active: true },
    { name: 'Cold Email', slug: 'cold-email', description: 'Cold email outreach', icon: 'envelope', order: 2, is_active: true },
    { name: 'LinkedIn', slug: 'linkedin', description: 'LinkedIn outreach or connection', icon: 'linkedin', order: 3, is_active: true },
    { name: 'Conference', slug: 'conference', description: 'Met at conference or trade show', icon: 'users', order: 4, is_active: true },
    { name: 'Referral', slug: 'referral', description: 'Referred by existing customer or contact', icon: 'user-friends', order: 5, is_active: true },
    { name: 'Website', slug: 'website', description: 'Inbound from company website', icon: 'globe', order: 6, is_active: true },
    { name: 'Partner', slug: 'partner', description: 'Through partnership or channel', icon: 'handshake', order: 7, is_active: true },
  ],
  leadTypes: [
    { name: 'Job Listing', slug: 'job-listing', description: 'Lead from job posting', icon: 'clipboard-list', is_active: true },
    { name: 'Conference Attendee', slug: 'conference-attendee', description: 'Met at conference', icon: 'ticket-alt', is_active: true },
    { name: 'Webinar Participant', slug: 'webinar', description: 'Attended webinar or online event', icon: 'video', is_active: true },
    { name: 'Direct Referral', slug: 'direct-referral', description: 'Directly referred by contact', icon: 'user-plus', is_active: true },
    { name: 'Inbound Lead', slug: 'inbound', description: 'Contacted us directly', icon: 'arrow-down', is_active: true },
    { name: 'Research Lead', slug: 'research', description: 'Identified through research', icon: 'search', is_active: true },
  ],
  prospectTypes: [
    { name: 'Individual', slug: 'individual', description: 'Individual contact person', icon: 'user', order: 1, is_active: true },
    { name: 'Organization', slug: 'organization', description: 'Company or organization', icon: 'building', order: 2, is_active: true },
    { name: 'Group', slug: 'group', description: 'Team or department', icon: 'users', order: 3, is_active: true },
    { name: 'Partnership', slug: 'partnership', description: 'Strategic partner or alliance', icon: 'handshake', order: 4, is_active: true },
  ],
};

const SalesModuleSettings = () => {
  const [activeTab, setActiveTab] = useState('statuses');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  const [activityTypes, setActivityTypes] = useState([]);
  const [outcomeTypes, setOutcomeTypes] = useState([]);
  const [prospectTypes, setProspectTypes] = useState([]);

  // Form states
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [stageType, setStageType] = useState('lead'); // 'lead', 'opportunity', or 'both'

  // Fetch data on mount and when tab changes
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'statuses': {
          const statusRes = await adminApi.get('/admin/sales/settings/statuses/');
          setStatuses(statusRes.data || []);
          break;
        }
        case 'priorities': {
          const priorityRes = await adminApi.get('/admin/sales/settings/priorities/');
          setPriorities(priorityRes.data || []);
          break;
        }
        case 'leadSources': {
          const leadSourceRes = await adminApi.get('/admin/sales/settings/lead-sources/');
          setLeadSources(leadSourceRes.data || []);
          break;
        }
        case 'leadTypes': {
          const leadTypeRes = await adminApi.get('/admin/sales/settings/lead-types/');
          setLeadTypes(leadTypeRes.data || []);
          break;
        }
        case 'activityTypes': {
          const activityRes = await adminApi.get('/admin/sales/settings/activity-types/');
          setActivityTypes(activityRes.data || []);
          break;
        }
        case 'outcomeTypes': {
          const outcomeRes = await adminApi.get('/admin/sales/settings/outcome-types/');
          setOutcomeTypes(outcomeRes.data || []);
          break;
        }
        case 'prospectTypes': {
          const prospectTypeRes = await adminApi.get('/admin/sales/settings/prospect-types/');
          setProspectTypes(prospectTypeRes.data || []);
          break;
        }
        default:
          break;
      }
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingItem(null);
    setFormData(getDefaultFormData());
    setStageType('lead'); // Default to lead stage
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    // Set stage type from applies_to field
    if (activeTab === 'statuses' && item.applies_to) {
      setStageType(item.applies_to);
    }
    setShowForm(true);
  };

  const handleSetDefault = async (item) => {
    try {
      const endpoint = getEndpoint();
      await adminApi.patch(`${endpoint}${item.id}/`, { is_default: true });
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
      return;
    }

    try {
      const endpoint = getEndpoint();
      await adminApi.delete(`${endpoint}${item.id}/`);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const endpoint = getEndpoint();

      if (editingItem) {
        // Update existing item
        const dataToSend = { ...formData };
        if (activeTab === 'statuses') {
          dataToSend.applies_to = stageType;
        }
        await adminApi.patch(`${endpoint}${editingItem.id}/`, dataToSend);
      } else {
        // Create new item(s)
        if (activeTab === 'statuses' && stageType === 'both') {
          // Create both lead and opportunity stages
          const leadData = { ...formData, applies_to: 'lead', order: formData.order || 0 };
          const oppData = { ...formData, applies_to: 'opportunity', order: (formData.order || 0) + 10 };

          await adminApi.post(endpoint, leadData);
          await adminApi.post(endpoint, oppData);
        } else {
          // Create single item
          const dataToSend = { ...formData };
          if (activeTab === 'statuses') {
            dataToSend.applies_to = stageType;
          }
          await adminApi.post(endpoint, dataToSend);
        }
      }

      setShowForm(false);
      setFormData({});
      setEditingItem(null);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDefaults = async () => {
    if (!window.confirm('Create all default options? This will add multiple items.')) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const endpoint = getEndpoint();
      const defaults = DEFAULT_OPTIONS[activeTab] || [];

      // Create all defaults sequentially
      for (const defaultItem of defaults) {
        await adminApi.post(endpoint, defaultItem);
      }

      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSingleDefault = async (defaultItem) => {
    setLoading(true);
    setError(null);

    try {
      const endpoint = getEndpoint();
      await adminApi.post(endpoint, defaultItem);
      await fetchData();
    } catch (err) {
      setError(parseDRFError(err));
    } finally {
      setLoading(false);
    }
  };

  const getEndpoint = () => {
    switch (activeTab) {
      case 'statuses':
        return '/admin/sales/settings/statuses/';
      case 'priorities':
        return '/admin/sales/settings/priorities/';
      case 'leadSources':
        return '/admin/sales/settings/lead-sources/';
      case 'leadTypes':
        return '/admin/sales/settings/lead-types/';
      case 'activityTypes':
        return '/admin/sales/settings/activity-types/';
      case 'outcomeTypes':
        return '/admin/sales/settings/outcome-types/';
      case 'prospectTypes':
        return '/admin/sales/settings/prospect-types/';
      default:
        return '';
    }
  };

  const getDefaultFormData = () => {
    const base = { name: '', slug: '', description: '', is_active: true };

    switch (activeTab) {
      case 'statuses':
        return { ...base, order: 0, is_default: false };
      case 'priorities':
        return { ...base, level: 0, color: '#808080' };
      case 'leadSources':
        return { ...base, icon: '', order: 0 };
      case 'leadTypes':
        return { ...base, icon: '' };
      case 'activityTypes':
        return { ...base, icon: '' };
      case 'outcomeTypes':
        return { ...base, is_positive: false, color: '#808080' };
      case 'prospectTypes':
        return { ...base, icon: '', order: 0 };
      default:
        return base;
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'statuses':
        return statuses;
      case 'priorities':
        return priorities;
      case 'leadSources':
        return leadSources;
      case 'leadTypes':
        return leadTypes;
      case 'activityTypes':
        return activityTypes;
      case 'outcomeTypes':
        return outcomeTypes;
      case 'prospectTypes':
        return prospectTypes;
      default:
        return [];
    }
  };

  // Update order when stage type changes
  useEffect(() => {
    if (activeTab === 'statuses' && !editingItem) {
      const currentData = getCurrentData();
      let newOrder = 0;

      if (stageType === 'lead') {
        // Find max order among lead statuses
        const leadOrders = currentData
          .filter(s => s.applies_to === 'lead' || s.applies_to === 'both')
          .map(s => s.order);
        newOrder = leadOrders.length > 0 ? Math.max(...leadOrders) + 1 : 0;
      } else if (stageType === 'opportunity') {
        // Find max order among opportunity statuses
        const oppOrders = currentData
          .filter(s => s.applies_to === 'opportunity' || s.applies_to === 'both')
          .map(s => s.order);
        newOrder = oppOrders.length > 0 ? Math.max(...oppOrders) + 1 : 0;
      } else if (stageType === 'both') {
        // Find max order across all statuses
        const allOrders = currentData.map(s => s.order);
        newOrder = allOrders.length > 0 ? Math.max(...allOrders) + 1 : 0;
      }

      setFormData(prev => ({ ...prev, order: newOrder }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageType, activeTab]);

  const renderStatusesTables = (data) => {
    // Separate statuses by applies_to field
    const leadStatuses = data.filter(s => s.applies_to === 'lead' || s.applies_to === 'both');
    const opportunityStatuses = data.filter(s => s.applies_to === 'opportunity' || s.applies_to === 'both');

    return (
      <div>
        {/* Lead Stages Table */}
        <div className="card mb-3">
          <div className="card-header bg-primary text-white">
            <h6 className="text-white mb-0">
              Lead Stages
            </h6>
            <small className="d-block mt-1">Qualification & Nurturing</small>
          </div>
          <div className="card-body">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {leadStatuses.length > 0 ? leadStatuses.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.name}
                      {item.is_default && (
                        <span className="badge bg-warning text-dark ms-2" title="Initial stage for new leads">
                          <i className="fas fa-star me-1"></i>Default
                        </span>
                      )}
                      <br />
                      <code className="small text-muted">{item.slug}</code>
                    </td>
                    <td>{item.order}</td>
                    <td>
                      {item.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      {!item.is_default && (
                        <button
                          className="btn btn-sm btn-outline-warning me-1"
                          title="Set as default (initial) stage for new leads"
                          onClick={() => handleSetDefault(item)}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => handleEdit(item)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center py-3">
                      <p className="text-muted mb-2">No lead stages defined.</p>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={loading}
                        onClick={async () => {
                          setLoading(true); setError(null);
                          try {
                            const ep = getEndpoint();
                            for (const s of DEFAULT_OPTIONS.leadStatuses) await adminApi.post(ep, s);
                            await fetchData();
                          } catch (err) { setError(parseDRFError(err)); }
                          finally { setLoading(false); }
                        }}
                      >
                        <i className="fas fa-magic me-2"></i>Create Default Lead Stages
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Opportunity Stages Table */}
        <div className="card mb-3">
          <div className="card-header bg-success text-white">
            <h6 className="text-white mb-0">
              Opportunity Stages
            </h6>
            <small className="d-block mt-1">Deal Closing Pipeline</small>
          </div>
          <div className="card-body">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {opportunityStatuses.length > 0 ? opportunityStatuses.map((item) => (
                  <tr key={item.id}>
                    <td>
                      {item.name}
                      {item.is_default && (
                        <span className="badge bg-warning text-dark ms-2" title="Initial stage for new opportunities">
                          <i className="fas fa-star me-1"></i>Default
                        </span>
                      )}
                      <br />
                      <code className="small text-muted">{item.slug}</code>
                    </td>
                    <td>{item.order}</td>
                    <td>
                      {item.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-secondary">Inactive</span>
                      )}
                    </td>
                    <td>
                      {!item.is_default && (
                        <button
                          className="btn btn-sm btn-outline-warning me-1"
                          title="Set as default (initial) stage for new opportunities"
                          onClick={() => handleSetDefault(item)}
                        >
                          <i className="fas fa-star"></i>
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-primary me-1"
                        onClick={() => handleEdit(item)}
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(item)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center py-3">
                      <p className="text-muted mb-2">No opportunity stages defined.</p>
                      <button
                        className="btn btn-success btn-sm"
                        disabled={loading}
                        onClick={async () => {
                          setLoading(true); setError(null);
                          try {
                            const ep = getEndpoint();
                            for (const s of DEFAULT_OPTIONS.opportunityStatuses) await adminApi.post(ep, s);
                            await fetchData();
                          } catch (err) { setError(parseDRFError(err)); }
                          finally { setLoading(false); }
                        }}
                      >
                        <i className="fas fa-magic me-2"></i>Create Default Opportunity Stages
                      </button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderDefaultOptions = () => {
    // Special handling for statuses tab - show both lead and opportunity defaults
    if (activeTab === 'statuses') {
      return (
        <div className="p-4">
          <div className="alert alert-info mb-4">
            <i className="fas fa-lightbulb me-2"></i>
            <strong>Quick Setup:</strong> Create suggested default stages for Leads and Opportunities.
            <div className="mt-2 small">
              <strong>Note:</strong> Lead stages are for qualification. Opportunity stages are for deal closing.
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-primary text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-user-plus me-2"></i>
                    Suggested Lead Stages
                  </h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Order</th>
                        <th style={{ width: '100px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DEFAULT_OPTIONS.leadStatuses.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.name}
                            <br />
                            <code className="small text-muted">{item.slug}</code>
                          </td>
                          <td>{item.order}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleCreateSingleDefault(item)}
                              disabled={loading}
                            >
                              <i className="fas fa-plus me-1"></i>
                              Create
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer">
                  <button
                    className="btn btn-primary btn-sm w-100"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        const endpoint = getEndpoint();
                        for (const defaultItem of DEFAULT_OPTIONS.leadStatuses) {
                          await adminApi.post(endpoint, defaultItem);
                        }
                        await fetchData();
                      } catch (err) {
                        setError(parseDRFError(err));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    <i className="fas fa-magic me-2"></i>
                    Create All Lead Stages
                  </button>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-success text-white">
                  <h6 className="mb-0">
                    <i className="fas fa-handshake me-2"></i>
                    Suggested Opportunity Stages
                  </h6>
                </div>
                <div className="card-body p-0">
                  <table className="table table-sm table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Order</th>
                        <th style={{ width: '100px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DEFAULT_OPTIONS.opportunityStatuses.map((item, index) => (
                        <tr key={index}>
                          <td>
                            {item.name}
                            <br />
                            <code className="small text-muted">{item.slug}</code>
                          </td>
                          <td>{item.order}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleCreateSingleDefault(item)}
                              disabled={loading}
                            >
                              <i className="fas fa-plus me-1"></i>
                              Create
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="card-footer">
                  <button
                    className="btn btn-success btn-sm w-100"
                    onClick={async () => {
                      setLoading(true);
                      setError(null);
                      try {
                        const endpoint = getEndpoint();
                        for (const defaultItem of DEFAULT_OPTIONS.opportunityStatuses) {
                          await adminApi.post(endpoint, defaultItem);
                        }
                        await fetchData();
                      } catch (err) {
                        setError(parseDRFError(err));
                      } finally {
                        setLoading(false);
                      }
                    }}
                    disabled={loading}
                  >
                    <i className="fas fa-magic me-2"></i>
                    Create All Opportunity Stages
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button
              className="btn btn-outline-success"
              onClick={async () => {
                if (!window.confirm('Create all default Lead and Opportunity stages?')) return;
                setLoading(true);
                setError(null);
                try {
                  const endpoint = getEndpoint();
                  for (const defaultItem of [...DEFAULT_OPTIONS.leadStatuses, ...DEFAULT_OPTIONS.opportunityStatuses]) {
                    await adminApi.post(endpoint, defaultItem);
                  }
                  await fetchData();
                } catch (err) {
                  setError(parseDRFError(err));
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
            >
              <i className="fas fa-magic me-2"></i>
              Create All Stages (Leads + Opportunities)
            </button>
          </div>
        </div>
      );
    }

    // Original default options for other tabs
    const defaults = DEFAULT_OPTIONS[activeTab] || [];
    return (
      <div className="p-4">
        <div className="alert alert-info mb-4">
          <i className="fas fa-lightbulb me-2"></i>
          <strong>Quick Setup:</strong> No items found. Create default options to get started.
        </div>

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Suggested Defaults</h6>
          <button
            className="btn btn-success btn-sm"
            onClick={handleCreateDefaults}
            disabled={loading}
          >
            <i className="fas fa-magic me-2"></i>
            Create All Defaults
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-sm table-bordered">
            <thead className="table-light">
              <tr>
                <th>Name</th>
                <th>Slug</th>
                {(activeTab === 'statuses' || activeTab === 'leadSources' || activeTab === 'prospectTypes') && <th>Order</th>}
                {activeTab === 'priorities' && <th>Level</th>}
                {(activeTab === 'priorities' || activeTab === 'outcomeTypes') && <th>Color</th>}
                {(activeTab === 'activityTypes' || activeTab === 'leadSources' || activeTab === 'leadTypes' || activeTab === 'prospectTypes') && <th>Icon</th>}
                {activeTab === 'outcomeTypes' && <th>Positive</th>}
                <th style={{ width: '100px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {defaults.map((item, index) => (
                <tr key={index}>
                  <td>{item.name}</td>
                  <td><code className="small">{item.slug}</code></td>
                  {(activeTab === 'statuses' || activeTab === 'leadSources' || activeTab === 'prospectTypes') && <td>{item.order}</td>}
                  {activeTab === 'priorities' && <td>{item.level}</td>}
                  {(activeTab === 'priorities' || activeTab === 'outcomeTypes') && (
                    <td>
                      <span
                        className="badge"
                        style={{ backgroundColor: item.color, color: '#fff' }}
                      >
                        {item.color}
                      </span>
                    </td>
                  )}
                  {(activeTab === 'activityTypes' || activeTab === 'leadSources' || activeTab === 'leadTypes' || activeTab === 'prospectTypes') && (
                    <td>
                      {item.icon && <i className={`fas fa-${item.icon} me-2`}></i>}
                      {item.icon}
                    </td>
                  )}
                  {activeTab === 'outcomeTypes' && (
                    <td>
                      {item.is_positive ? (
                        <i className="fas fa-check-circle text-success"></i>
                      ) : (
                        <i className="fas fa-times-circle text-muted"></i>
                      )}
                    </td>
                  )}
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleCreateSingleDefault(item)}
                      disabled={loading}
                    >
                      <i className="fas fa-plus me-1"></i>
                      Create
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const data = getCurrentData();

    if (loading) {
      return (
        <div className="text-center p-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (data.length === 0) {
      return renderDefaultOptions();
    }

    // Special handling for statuses - show separate Lead and Opportunity tables
    if (activeTab === 'statuses') {
      return renderStatusesTables(data);
    }

    // Original single table for other tabs
    return (
      <table className="table table-hover">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            {(activeTab === 'statuses' || activeTab === 'leadSources' || activeTab === 'prospectTypes') && <th>Order</th>}
            {activeTab === 'priorities' && <th>Level</th>}
            {(activeTab === 'priorities' || activeTab === 'outcomeTypes') && <th>Color</th>}
            {(activeTab === 'activityTypes' || activeTab === 'leadSources' || activeTab === 'leadTypes' || activeTab === 'prospectTypes') && <th>Icon</th>}
            {activeTab === 'outcomeTypes' && <th>Positive</th>}
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={item.id}>
              <td>{item.name}</td>
              <td><code>{item.slug}</code></td>
              {(activeTab === 'statuses' || activeTab === 'leadSources' || activeTab === 'prospectTypes') && <td>{item.order}</td>}
              {activeTab === 'priorities' && <td>{item.level}</td>}
              {(activeTab === 'priorities' || activeTab === 'outcomeTypes') && (
                <td>
                  <span
                    className="badge"
                    style={{ backgroundColor: item.color, color: '#fff' }}
                  >
                    {item.color}
                  </span>
                </td>
              )}
              {(activeTab === 'activityTypes' || activeTab === 'leadSources' || activeTab === 'leadTypes' || activeTab === 'prospectTypes') && (
                <td>
                  {item.icon && <i className={`fas fa-${item.icon} me-2`}></i>}
                  {item.icon}
                </td>
              )}
              {activeTab === 'outcomeTypes' && (
                <td>
                  {item.is_positive ? (
                    <i className="fas fa-check-circle text-success"></i>
                  ) : (
                    <i className="fas fa-times-circle text-muted"></i>
                  )}
                </td>
              )}
              <td>
                {item.is_active ? (
                  <span className="badge bg-success">Active</span>
                ) : (
                  <span className="badge bg-secondary">Inactive</span>
                )}
              </td>
              <td>
                <button
                  className="btn btn-sm btn-outline-primary me-1"
                  onClick={() => handleEdit(item)}
                >
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => handleDelete(item)}
                >
                  <i className="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderForm = () => {
    return (
      <form onSubmit={handleSave}>
        <div className="mb-3">
          <label className="form-label">Name *</label>
          <input
            type="text"
            className="form-control"
            value={formData.name || ''}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Slug *</label>
          <input
            type="text"
            className="form-control"
            value={formData.slug || ''}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
            pattern="[a-z0-9-]+"
            title="Only lowercase letters, numbers, and hyphens"
          />
          <small className="form-text text-muted">
            Lowercase letters, numbers, and hyphens only
          </small>
        </div>

        <div className="mb-3">
          <label className="form-label">Description</label>
          <textarea
            className="form-control"
            rows="3"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
        </div>

        {/* Stage Type Selector (for statuses) */}
        {activeTab === 'statuses' && (
          <div className="mb-3">
            <label className="form-label">Applies To *</label>
            <div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="appliesLead"
                  checked={stageType === 'lead' || stageType === 'both'}
                  onChange={(e) => {
                    const isLeadChecked = e.target.checked;
                    const isOppChecked = stageType === 'opportunity' || stageType === 'both';

                    if (isLeadChecked && isOppChecked) {
                      setStageType('both');
                    } else if (isLeadChecked) {
                      setStageType('lead');
                    } else if (isOppChecked) {
                      setStageType('opportunity');
                    } else {
                      // At least one must be checked, revert
                      setStageType('lead');
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="appliesLead">
                  Lead (Qualification & Nurturing)
                </label>
              </div>
              <div className="form-check form-check-inline">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="appliesOpportunity"
                  checked={stageType === 'opportunity' || stageType === 'both'}
                  onChange={(e) => {
                    const isOppChecked = e.target.checked;
                    const isLeadChecked = stageType === 'lead' || stageType === 'both';

                    if (isLeadChecked && isOppChecked) {
                      setStageType('both');
                    } else if (isOppChecked) {
                      setStageType('opportunity');
                    } else if (isLeadChecked) {
                      setStageType('lead');
                    } else {
                      // At least one must be checked, revert
                      setStageType('opportunity');
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="appliesOpportunity">
                  Opportunity (Deal Closing)
                </label>
              </div>
            </div>
            <small className="form-text text-muted">
              {stageType === 'both'
                ? (editingItem
                  ? 'This stage appears in both Lead and Opportunity pipelines'
                  : 'This stage appears in both Lead and Opportunity pipelines')
                : stageType === 'lead'
                ? 'This stage appears only in the Lead pipeline'
                : 'This stage appears only in the Opportunity pipeline'}
            </small>
          </div>
        )}

        {activeTab === 'statuses' && (
          <div className="mb-3">
            <label className="form-label">Display Order</label>
            <input
              type="number"
              className="form-control"
              value={formData.order || 0}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              readOnly={!editingItem}
            />
            <small className="form-text text-muted">
              {editingItem
                ? 'You can manually adjust the order value'
                : 'Order is automatically set based on stage type'}
            </small>
          </div>
        )}

        {activeTab === 'statuses' && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isDefault"
              checked={formData.is_default || false}
              onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="isDefault">
              {/* <i className="fas fa-star text-warning me-1"></i> */}
              Set as default (initial) stage for new {stageType === 'opportunity' ? 'opportunities' : 'leads'}
            </label>
            <div className="form-text text-muted">
              Only one stage per type can be the default. Setting this will unset the current default.
            </div>
          </div>
        )}

        {(activeTab === 'leadSources' || activeTab === 'prospectTypes') && (
          <div className="mb-3">
            <label className="form-label">Display Order</label>
            <input
              type="number"
              className="form-control"
              value={formData.order || 0}
              onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
            />
          </div>
        )}

        {activeTab === 'priorities' && (
          <div className="mb-3">
            <label className="form-label">Priority Level (higher = more urgent)</label>
            <input
              type="number"
              className="form-control"
              value={formData.level || 0}
              onChange={(e) => setFormData({ ...formData, level: parseInt(e.target.value) })}
            />
          </div>
        )}

        {(activeTab === 'priorities' || activeTab === 'outcomeTypes') && (
          <div className="mb-3">
            <label className="form-label">Color</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={formData.color || '#808080'}
              onChange={(e) => setFormData({ ...formData, color: e.target.value })}
            />
          </div>
        )}

        {(activeTab === 'activityTypes' || activeTab === 'leadSources' || activeTab === 'leadTypes' || activeTab === 'prospectTypes') && (
          <div className="mb-3">
            <label className="form-label">Icon (FontAwesome)</label>
            <input
              type="text"
              className="form-control"
              value={formData.icon || ''}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              placeholder="e.g., phone, envelope, linkedin, user, building"
            />
            <small className="form-text text-muted">
              FontAwesome icon name (without fa- prefix)
            </small>
          </div>
        )}

        {activeTab === 'outcomeTypes' && (
          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="isPositive"
              checked={formData.is_positive || false}
              onChange={(e) => setFormData({ ...formData, is_positive: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="isPositive">
              Positive Outcome
            </label>
          </div>
        )}

        <div className="mb-3 form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="isActive"
            checked={formData.is_active !== false}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />
          <label className="form-check-label" htmlFor="isActive">
            Active
          </label>
        </div>

        <div className="d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                Save
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowForm(false);
              setFormData({});
              setEditingItem(null);
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  return (
    <div className="sales-module-settings p-3">
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Tabs */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'statuses' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('statuses');
              setShowForm(false);
            }}
          >
            Lead/Opportunity Stages
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'priorities' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('priorities');
              setShowForm(false);
            }}
          >
            Priorities
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'leadSources' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('leadSources');
              setShowForm(false);
            }}
          >
            Lead Sources
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'leadTypes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('leadTypes');
              setShowForm(false);
            }}
          >
            Lead Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'activityTypes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('activityTypes');
              setShowForm(false);
            }}
          >
            Activity Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'outcomeTypes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('outcomeTypes');
              setShowForm(false);
            }}
          >
            Outcome Types
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'prospectTypes' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('prospectTypes');
              setShowForm(false);
            }}
          >
            Prospect Types
          </button>
        </li>
      </ul>

      {/* Action Bar */}
      {!showForm && (
        <div className="d-flex justify-content-end me-2 mb-3">
          {/* <h5 className="mb-0">
            {activeTab === 'statuses' && ''}
            {activeTab === 'priorities' && 'Priority Levels'}
            {activeTab === 'leadSources' && 'Lead Sources'}
            {activeTab === 'leadTypes' && 'Lead Types'}
            {activeTab === 'activityTypes' && 'Activity Types'}
            {activeTab === 'outcomeTypes' && 'Outcome Types'}
          </h5> */}
          <button className="btn btn-primary btn-sm" onClick={handleCreate}>
            <i className="fas fa-plus me-2"></i>
            Add New
          </button>
        </div>
      )}

      {/* Content */}
      {showForm ? (
        <div className="card">
            <div className="card-body">
            <h6 className="card-title mb-3">
              {editingItem ? 'Edit' : 'Create New'}{' '}
              {activeTab === 'statuses' && 'Stage'}
              {activeTab === 'priorities' && 'Priority'}
              {activeTab === 'leadSources' && 'Lead Source'}
              {activeTab === 'leadTypes' && 'Lead Type'}
              {activeTab === 'activityTypes' && 'Activity Type'}
              {activeTab === 'outcomeTypes' && 'Outcome Type'}
              {activeTab === 'prospectTypes' && 'Prospect Type'}
            </h6>
            {renderForm()}
            </div>
        </div>
      ) : (
        <div className="card border-0 box-shadow-0">
            {renderTable()}
        </div>
      )}
    </div>
  );
};

export default SalesModuleSettings;
